// SPDX-License-Identifier: AGPL-3.0-or-later
import {Endpoints} from '@app/features/app/constants/Endpoints';
import Channels from '@app/features/channel/state/Channels';
import Guilds from '@app/features/guild/state/Guilds';
import GuildMembers from '@app/features/member/state/GuildMembers';
import type {Message as MessageModel} from '@app/features/messaging/models/Message';
import Messages from '@app/features/messaging/state/Messages';
import AutoAck from '@app/features/notification/state/AutoAck';
import Permission from '@app/features/permissions/state/Permission';
import {conduit} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import Relationships from '@app/features/relationship/state/Relationships';
import Dimension from '@app/features/ui/state/Dimension';
import UserGuildSettings from '@app/features/user/state/UserGuildSettings';
import Users from '@app/features/user/state/Users';
import {ChannelTypes, Permissions} from '@app/vendor/constants/ChannelConstants';
import {MS_PER_DAY} from '@app/vendor/date_utils/DateConstants';
import type {ChannelId, GuildId} from '@app/vendor/schema/branded/WireIds';
import type {Message as WireMessage} from '@app/vendor/schema/domains/message/MessageResponseSchemas';
import {compare as compareSnowflakes, extractTimestamp, fromTimestamp} from '@app/vendor/snowflake/SnowflakeUtils';
import {observable, runInAction} from 'mobx';

const logger = new Logger('ReadStates');
const CAN_READ_PERMISSIONS = Permissions.VIEW_CHANNEL;
const OLD_MESSAGE_AGE_THRESHOLD = 7 * MS_PER_DAY;
const RECENT_MESSAGE_THRESHOLD = 3 * MS_PER_DAY;
const ACK_BATCH_DELAY_MS = 3000;
const ACK_BATCH_SIZE = 100;
const ACK_RETRY_BASE_DELAY_MS = 5000;
const ACK_RETRY_MAX_DELAY_MS = 60000;
type Timer = ReturnType<typeof setTimeout>;
export interface GatewayReadState {
	id: string;
	mention_count?: number;
	last_message_id?: string | null;
	last_pin_timestamp?: string | null;
}
interface ChannelPayload {
	id: string;
	type: number;
	guild_id?: string;
	last_message_id?: string | null;
	last_pin_timestamp?: string | null;
}
interface AckOptions {
	messageId?: string | null;
	local?: boolean;
	immediate?: boolean;
	force?: boolean;
	isExplicitUserAction?: boolean;
	preserveStickyUnread?: boolean;
}
interface AppliedAck {
	acked: boolean;
	messageId: string | null;
	hadMentions: boolean;
}
interface PendingAck {
	channelId: string;
	messageId: string;
	deadline: number;
	attempt: number;
}
interface ArchivedReadState {
	ackMessageId: string | null;
	ackPinTimestamp: number;
}
function parseTimestamp(timestamp?: string | null): number {
	if (timestamp == null) return 0;
	const parsed = Date.parse(timestamp);
	return Number.isNaN(parsed) ? 0 : parsed;
}
function snowflakeTimestamp(messageId?: string | null): number {
	if (messageId == null) return 0;
	const timestamp = extractTimestamp(messageId);
	return Number.isFinite(timestamp) ? timestamp : 0;
}
function compareMessageIds(a?: string | null, b?: string | null): number {
	if (a == null && b == null) return 0;
	if (a == null) return -1;
	if (b == null) return 1;
	return compareSnowflakes(a, b);
}
function isNewerMessageId(a?: string | null, b?: string | null): boolean {
	return compareMessageIds(a, b) > 0;
}
function normalizeCount(count: number | undefined | null): number {
	if (count == null || !Number.isFinite(count)) return 0;
	return Math.max(0, Math.floor(count));
}
function chunkEntries<T>(entries: Array<T>, size: number): Array<Array<T>> {
	const chunks: Array<Array<T>> = [];
	for (let i = 0; i < entries.length; i += size) {
		chunks.push(entries.slice(i, i + size));
	}
	return chunks;
}
class ReadStateEntry {
	readonly channelId: string;
	_guildId: string | null = null;
	loadedMessages = false;
	private _lastMessageId: string | null = null;
	private _lastMessageTimestamp = 0;
	private _ackMessageId: string | null = null;
	private _ackMessageTimestamp = 0;
	ackPinTimestamp = 0;
	lastPinTimestamp = 0;
	isManualAck = false;
	private _oldestUnreadMessageId: string | null = null;
	oldestUnreadMessageIdStale = false;
	private _stickyUnreadMessageId: string | null = null;
	estimated = false;
	private _unreadCount = 0;
	private _mentionCount = 0;
	outgoingAck: string | null = null;
	snapshot?: {
		unread: boolean;
		mentionCount: number;
		guildUnread: boolean | null;
		guildMentionCount: number | null;
		takenAt: number;
	};
	constructor(channelId: string) {
		this.channelId = channelId;
	}
	private get now(): number {
		return Date.now();
	}
	get guildId(): string | null {
		const channel = Channels.getChannel(this.channelId);
		return channel?.guildId ?? this._guildId ?? null;
	}
	get lastMessageId(): string | null {
		return this._lastMessageId;
	}
	set lastMessageId(messageId: string | null) {
		this._lastMessageId = messageId;
		this._lastMessageTimestamp = snowflakeTimestamp(messageId);
	}
	get lastMessageTimestamp(): number {
		return this._lastMessageTimestamp;
	}
	get ackMessageId(): string | null {
		return this._ackMessageId;
	}
	set ackMessageId(messageId: string | null) {
		this._ackMessageId = messageId;
		this._ackMessageTimestamp = snowflakeTimestamp(messageId);
	}
	get oldestUnreadMessageId(): string | null {
		return this._oldestUnreadMessageId;
	}
	set oldestUnreadMessageId(messageId: string | null) {
		this._oldestUnreadMessageId = messageId;
		this.oldestUnreadMessageIdStale = false;
	}
	get stickyUnreadMessageId(): string | null {
		return this._stickyUnreadMessageId;
	}
	set stickyUnreadMessageId(messageId: string | null) {
		this._stickyUnreadMessageId = messageId;
	}
	get visualUnreadMessageId(): string | null {
		return this._stickyUnreadMessageId ?? this._oldestUnreadMessageId;
	}
	clearStickyUnread(): void {
		this._stickyUnreadMessageId = null;
	}
	get unreadCount(): number {
		return this._unreadCount;
	}
	set unreadCount(count: number) {
		this._unreadCount = normalizeCount(count);
	}
	get mentionCount(): number {
		return this._mentionCount;
	}
	set mentionCount(count: number) {
		this._mentionCount = normalizeCount(count);
	}
	get oldestUnreadTimestamp(): number {
		return snowflakeTimestamp(this.oldestUnreadMessageId);
	}
	get ackTimestamp(): number {
		if (this._ackMessageTimestamp !== 0 && !Number.isNaN(this._ackMessageTimestamp)) {
			return this._ackMessageTimestamp;
		}
		this.computeAckTimestamp();
		return this._ackMessageTimestamp;
	}
	private computeAckTimestamp(): void {
		const guildId = this.guildId;
		if (guildId != null) {
			const guild = Guilds.getGuild(guildId);
			if (guild != null && guild.joinedAt != null) {
				const joinTimestamp = Date.parse(guild.joinedAt);
				if (!Number.isNaN(joinTimestamp)) {
					this._ackMessageTimestamp = joinTimestamp;
					this._ackMessageId = fromTimestamp(joinTimestamp);
					return;
				}
			}
		}
		const channelTimestamp = snowflakeTimestamp(this.channelId);
		if (channelTimestamp === 0 || Number.isNaN(channelTimestamp)) {
			this._ackMessageTimestamp = this.now;
			return;
		}
		this._ackMessageTimestamp = channelTimestamp;
		this._ackMessageId = fromTimestamp(channelTimestamp);
	}
	get isPrivate(): boolean {
		const channel = Channels.getChannel(this.channelId);
		return channel?.isPrivate() ?? false;
	}
	canTrackUnreads(): boolean {
		const channel = Channels.getChannel(this.channelId);
		if (channel == null) {
			return false;
		}
		return channel.isPrivate() || Permission.can(CAN_READ_PERMISSIONS, channel);
	}
	canBeUnread(): boolean {
		if (!this.canTrackUnreads()) {
			return false;
		}
		const guildId = this.guildId;
		if (guildId == null) return true;
		const now = this.now;
		if (this._lastMessageTimestamp < now - OLD_MESSAGE_AGE_THRESHOLD) {
			if (!this.hasRecentlyVisitedAndRead(now) && !this.hasMentions()) {
				return false;
			}
		}
		return true;
	}
	canHaveMentions(): boolean {
		if (this.mentionCount === 0) {
			return false;
		}
		if (!this.canTrackUnreads()) {
			return false;
		}
		const guildId = this.guildId;
		if (guildId == null) {
			return true;
		}
		const now = this.now;
		if (this._lastMessageTimestamp < now - OLD_MESSAGE_AGE_THRESHOLD) {
			return false;
		}
		return true;
	}
	hasUnread(): boolean {
		const ackTimestamp = this.ackTimestamp;
		const lastMessageTimestamp = this._lastMessageTimestamp;
		if (
			Number.isNaN(ackTimestamp) ||
			Number.isNaN(lastMessageTimestamp) ||
			ackTimestamp === 0 ||
			lastMessageTimestamp === 0
		) {
			return false;
		}
		return ackTimestamp < lastMessageTimestamp;
	}
	hasMentions(): boolean {
		return this.mentionCount > 0;
	}
	hasUnreadOrMentions(): boolean {
		return this.hasUnread() || this.hasMentions();
	}
	hasRecentlyVisitedAndRead(now = this.now): boolean {
		if (this._lastMessageTimestamp === 0) return false;
		if (this._ackMessageId == null) return false;
		const ackTime = this.ackTimestamp;
		return ackTime > now - RECENT_MESSAGE_THRESHOLD;
	}
	getGuildChannelUnreadState(
		channel: {
			isPrivate(): boolean;
			guildId?: string;
		},
		isOptInEnabled: boolean,
		isChannelMuted: boolean,
		isGuildMuted: boolean,
	): {
		mentionCount: number;
		unread: boolean;
	} {
		const now = this.now;
		if (isOptInEnabled && this._lastMessageTimestamp < now - OLD_MESSAGE_AGE_THRESHOLD) {
			if (!this.hasRecentlyVisitedAndRead(now) && this.mentionCount <= 0) {
				return {mentionCount: 0, unread: false};
			}
		}
		if (!channel.isPrivate() && !this.canTrackUnreads()) {
			return {mentionCount: 0, unread: false};
		}
		if (isChannelMuted || isGuildMuted) {
			return {mentionCount: this.mentionCount, unread: false};
		}
		const ackTimestamp = this.ackTimestamp;
		const lastMessageTimestamp = this._lastMessageTimestamp;
		const unread =
			ackTimestamp !== 0 &&
			!Number.isNaN(ackTimestamp) &&
			lastMessageTimestamp !== 0 &&
			!Number.isNaN(lastMessageTimestamp) &&
			ackTimestamp < lastMessageTimestamp;
		return {
			mentionCount: this.mentionCount,
			unread,
		};
	}
	rebuild(
		ackMessageId?: string | null,
		{
			recomputeMentions = false,
		}: {
			recomputeMentions?: boolean;
		} = {},
	): void {
		this.ackMessageId = ackMessageId ?? this._ackMessageId;
		this.oldestUnreadMessageId = null;
		this.estimated = false;
		this.unreadCount = 0;
		if (recomputeMentions) {
			this.mentionCount = 0;
		}
		if (!this.hasUnread()) {
			return;
		}
		const currentUser = Users.getCurrentUser();
		if (currentUser == null) {
			return;
		}
		const messages = Messages.getMessages(this.channelId);
		const ackTimestamp = this.ackTimestamp;
		const isPrivate = this.isPrivate;
		const userId = currentUser.id;
		const guildId = this.guildId;
		const channelId = this.channelId;
		const suppressEveryone = recomputeMentions ? UserGuildSettings.isSuppressEveryoneEnabled(guildId) : false;
		const suppressRoles = recomputeMentions ? UserGuildSettings.isSuppressRolesEnabled(guildId) : false;
		const isMuted = recomputeMentions ? UserGuildSettings.isGuildOrChannelMuted(guildId, channelId) : false;
		const member = recomputeMentions && guildId ? GuildMembers.getMember(guildId, userId) : null;
		const memberRoles = member?.roles ?? null;
		let foundAckMessage = false;
		let loadedOlderMessages = false;
		let oldestUnread: string | null = null;
		messages.forAll((message) => {
			if (!foundAckMessage) {
				foundAckMessage = message.id === this._ackMessageId;
			} else if (this._oldestUnreadMessageId == null) {
				this._oldestUnreadMessageId = message.id;
			}
			if (snowflakeTimestamp(message.id) > ackTimestamp) {
				this.unreadCount++;
				if (recomputeMentions && !Relationships.isBlocked(message.author.id)) {
					const mentions = message.mentions;
					const mentionEveryone = message.mentionEveryone;
					const mentionRoles = message.mentionRoles;
					const hasUserMention = mentions?.some((m) => m.id === userId) ?? false;
					const hasEveryoneMention = !suppressEveryone && !!mentionEveryone;
					let hasRoleMention = false;
					if (!suppressRoles && memberRoles && mentionRoles && mentionRoles.length > 0) {
						for (const roleId of mentionRoles) {
							if (memberRoles.has(roleId)) {
								hasRoleMention = true;
								break;
							}
						}
					}
					let shouldMention = hasUserMention || hasEveryoneMention || hasRoleMention;
					if (!shouldMention && isPrivate && !isMuted) {
						shouldMention = true;
					}
					if (shouldMention) {
						this.mentionCount++;
					}
				}
				oldestUnread ??= message.id;
			} else {
				loadedOlderMessages = true;
			}
		});
		this.estimated =
			!messages.hasPresent() || (!(foundAckMessage || loadedOlderMessages) && messages.length === this.unreadCount);
		this.oldestUnreadMessageId = this._oldestUnreadMessageId ?? oldestUnread;
	}
	shouldMentionFor(message: MessageModel | WireMessage, userId: string, isPrivate: boolean): boolean {
		if (Relationships.isBlocked(message.author.id)) {
			return false;
		}
		const suppressEveryone = UserGuildSettings.isSuppressEveryoneEnabled(this.guildId);
		const suppressRoles = UserGuildSettings.isSuppressRolesEnabled(this.guildId);
		const mentions = message.mentions;
		const mentionEveryone = 'mentionEveryone' in message ? message.mentionEveryone : message.mention_everyone;
		const mentionRoles = 'mentionRoles' in message ? message.mentionRoles : message.mention_roles;
		const hasUserMention = mentions?.some((m) => m.id === userId) ?? false;
		const hasEveryoneMention = !suppressEveryone && !!mentionEveryone;
		const hasRoleMention =
			!suppressRoles &&
			(mentionRoles?.length ?? 0) > 0 &&
			(() => {
				const guildId = this.guildId;
				if (!guildId) {
					return false;
				}
				const member = GuildMembers.getMember(guildId, userId);
				if (!member) {
					return false;
				}
				return mentionRoles?.some((roleId: string) => member.roles.has(roleId)) ?? false;
			})();
		let shouldMention = hasUserMention || hasEveryoneMention || hasRoleMention;
		const isMuted = UserGuildSettings.isGuildOrChannelMuted(this.guildId, this.channelId);
		if (!shouldMention && isPrivate && !isMuted) {
			shouldMention = true;
		}
		return shouldMention;
	}
	computeMentionCountAfterAck(messageId: string): number {
		const currentUser = Users.getCurrentUser();
		if (currentUser == null) {
			return 0;
		}
		const ackTimestamp = snowflakeTimestamp(messageId);
		if (ackTimestamp === 0 || Number.isNaN(ackTimestamp)) {
			return 0;
		}
		const messages = Messages.getMessages(this.channelId);
		const isPrivate = this.isPrivate;
		let mentionCount = 0;
		messages.forAll((message) => {
			if (snowflakeTimestamp(message.id) <= ackTimestamp) {
				return;
			}
			if (this.shouldMentionFor(message, currentUser.id, isPrivate)) {
				mentionCount++;
			}
		});
		return mentionCount;
	}
}
class ReadStates {
	private readonly states = new Map<ChannelId, ReadStateEntry>();
	private readonly archivedStates = new Map<ChannelId, ArchivedReadState>();
	private readonly mentionChannels = new Set<ChannelId>();
	private readonly listeners = new Set<() => void>();
	private readonly versionBox = observable.box(0);
	private readonly pendingAcks = new Map<ChannelId, PendingAck>();
	updateCounter = 0;
	private pendingChanges = new Map<ChannelId, GuildId | null>();
	private pendingGlobalRecompute = false;
	private ackFlushTimer: Timer | null = null;
	private isFlushingAcks = false;
	get version(): number {
		return this.versionBox.get();
	}
	private setVersion(version: number): void {
		this.updateCounter = version;
		runInAction(() => {
			this.versionBox.set(version);
		});
	}
	private bumpVersion(): void {
		this.setVersion(this.updateCounter + 1);
		for (const listener of Array.from(this.listeners)) {
			listener();
		}
	}
	private refreshMentionChannel(channelId: string): void {
		const state = this.states.get(channelId as ChannelId);
		if (state != null && state.mentionCount > 0 && state.canHaveMentions()) {
			this.mentionChannels.add(channelId as ChannelId);
		} else {
			this.mentionChannels.delete(channelId as ChannelId);
		}
	}
	private rebuildMentionChannels(): void {
		this.mentionChannels.clear();
		for (const channelId of this.states.keys()) {
			this.refreshMentionChannel(channelId);
		}
	}
	private setMentionCount(state: ReadStateEntry, mentionCount: number): void {
		state.mentionCount = mentionCount;
		this.refreshMentionChannel(state.channelId);
	}
	private notifyChange(
		channelId?: string,
		{
			global = false,
			guildIdOverride,
		}: {
			global?: boolean;
			guildIdOverride?: string | null;
		} = {},
	): void {
		if (global) {
			this.rebuildMentionChannels();
			this.pendingGlobalRecompute = true;
			this.pendingChanges.clear();
		} else if (channelId != null && !this.pendingGlobalRecompute) {
			this.refreshMentionChannel(channelId);
			const entry = this.states.get(channelId as ChannelId);
			const guildId = guildIdOverride !== undefined ? guildIdOverride : (entry?.guildId ?? null);
			this.pendingChanges.set(channelId as ChannelId, guildId as GuildId | null);
		}
		this.bumpVersion();
	}
	consumePendingChanges(): {
		all: boolean;
		channelIds: Array<ChannelId>;
		changes: Array<{
			channelId: string;
			guildId: string | null;
		}>;
	} {
		const all = this.pendingGlobalRecompute;
		const changes: Array<{
			channelId: string;
			guildId: string | null;
		}> = [];
		const channelIds: Array<ChannelId> = [];
		if (!all) {
			for (const [channelId, guildId] of this.pendingChanges.entries()) {
				channelIds.push(channelId);
				changes.push({channelId, guildId});
			}
		}
		this.pendingGlobalRecompute = false;
		this.pendingChanges.clear();
		return {all, channelIds, changes};
	}
	get(channelId: string): ReadStateEntry {
		let entry = this.states.get(channelId as ChannelId);
		if (entry == null) {
			entry = new ReadStateEntry(channelId);
			this.states.set(channelId as ChannelId, entry);
		}
		return entry;
	}
	getIfExists(channelId: string): ReadStateEntry | undefined {
		return this.states.get(channelId as ChannelId);
	}
	clear(channelId: string): boolean {
		const entry = this.states.get(channelId as ChannelId);
		if (entry == null) {
			return false;
		}
		const guildId = entry.guildId;
		this.cancelPendingAck(channelId);
		this.states.delete(channelId as ChannelId);
		this.mentionChannels.delete(channelId as ChannelId);
		this.notifyChange(channelId, {guildIdOverride: guildId});
		return true;
	}
	private archiveState(channelId: string): void {
		const entry = this.states.get(channelId as ChannelId);
		if (entry == null) {
			return;
		}
		this.archivedStates.set(channelId as ChannelId, {
			ackMessageId: entry.ackMessageId,
			ackPinTimestamp: entry.ackPinTimestamp,
		});
	}
	clearAll(): void {
		this.reset();
		this.notifyChange(undefined, {global: true});
	}
	private reset(): void {
		this.states.clear();
		this.archivedStates.clear();
		this.mentionChannels.clear();
		this.pendingChanges.clear();
		this.pendingGlobalRecompute = false;
		this.pendingAcks.clear();
		this.clearAckFlushTimer();
	}
	get mentionChannelIds(): Array<ChannelId> {
		const ids: Array<ChannelId> = [];
		for (const channelId of Array.from(this.mentionChannels)) {
			const state = this.getIfExists(channelId);
			if (state?.canHaveMentions()) {
				ids.push(channelId);
			} else {
				this.mentionChannels.delete(channelId);
			}
		}
		return ids;
	}
	isAutomaticAckEnabled(channelId: string): boolean {
		return AutoAck.isAutomaticAckEnabled(channelId);
	}
	getUnreadCount(channelId: string): number {
		this.versionBox.get();
		const state = this.getIfExists(channelId);
		if (state == null || !state.canBeUnread() || !state.hasUnread()) return 0;
		return state.unreadCount;
	}
	getMentionCount(channelId: string): number {
		this.versionBox.get();
		const state = this.getIfExists(channelId);
		if (state == null || !state.canHaveMentions() || !state.hasUnread()) return 0;
		return state.mentionCount;
	}
	getManualAckMentionCount(channelId: string, messageId: string): number {
		const state = this.getIfExists(channelId);
		return state?.computeMentionCountAfterAck(messageId) ?? 0;
	}
	hasUnread(channelId: string): boolean {
		this.versionBox.get();
		const state = this.getIfExists(channelId);
		return !!(state?.canBeUnread() && state.hasUnread());
	}
	hasUnreadOrMentions(channelId: string): boolean {
		this.versionBox.get();
		const state = this.getIfExists(channelId);
		return !!(state?.canBeUnread() && state.hasUnreadOrMentions());
	}
	ackMessageId(channelId: string): string | null {
		const state = this.getIfExists(channelId);
		return state?.canBeUnread() ? state.ackMessageId : null;
	}
	lastMessageId(channelId: string): string | null {
		const state = this.getIfExists(channelId);
		return state?.lastMessageId ?? null;
	}
	getOldestUnreadMessageId(channelId: string): string | null {
		const state = this.getIfExists(channelId);
		return state?.canTrackUnreads() ? state.oldestUnreadMessageId : null;
	}
	getVisualUnreadMessageId(channelId: string): string | null {
		const state = this.getIfExists(channelId);
		return state?.canTrackUnreads() ? state.visualUnreadMessageId : null;
	}
	getChannelIds(): Array<ChannelId> {
		return Array.from(this.states.keys());
	}
	clearStickyUnread(channelId: string): void {
		const state = this.getIfExists(channelId);
		if (state != null) {
			state.clearStickyUnread();
			this.notifyChange(channelId);
		}
	}
	hasUnreadPins(channelId: string): boolean {
		const state = this.getIfExists(channelId);
		return !!(state?.canBeUnread() && state.lastPinTimestamp > state.ackPinTimestamp);
	}
	ackPins(channelId: string): void {
		const state = this.get(channelId);
		if (!this.applyPinAck(state)) {
			return;
		}
		conduit.post(Endpoints.CHANNEL_PINS_ACK(channelId)).catch((error) => {
			logger.error(`Failed to ack pins for ${channelId}:`, error);
		});
		this.notifyChange(channelId);
	}
	handleConnectionOpen(action: {readState: Array<GatewayReadState>; channels: Array<ChannelPayload>}): void {
		this.reset();
		const channelsWithReadState = new Set<ChannelId>();
		for (const readState of action.readState) {
			channelsWithReadState.add(readState.id as ChannelId);
			const state = this.get(readState.id);
			this.setMentionCount(state, readState.mention_count ?? 0);
			state.ackMessageId = readState.last_message_id ?? null;
			state.ackPinTimestamp = parseTimestamp(readState.last_pin_timestamp);
		}
		for (const channel of action.channels) {
			if (channel.type === ChannelTypes.GUILD_VOICE) continue;
			const state = this.get(channel.id);
			state.lastMessageId = channel.last_message_id ?? null;
			state.lastPinTimestamp = parseTimestamp(channel.last_pin_timestamp);
			state._guildId = channel.guild_id ?? null;
			if (!channelsWithReadState.has(channel.id as ChannelId)) {
				state.ackMessageId = null;
				this.setMentionCount(state, 0);
			}
			if (state.hasUnread()) {
				state.estimated = true;
				state.unreadCount = Math.max(1, state.mentionCount);
			}
		}
		this.notifyChange(undefined, {global: true});
	}
	handleGuildCreate(action: {
		guild: {
			id: string;
			channels?: ReadonlyArray<ChannelPayload>;
		};
	}): void {
		if (action.guild.channels) {
			for (const channel of action.guild.channels) {
				if (channel.type === ChannelTypes.GUILD_VOICE) continue;
				const state = this.get(channel.id);
				state.lastMessageId = channel.last_message_id ?? null;
				state.lastPinTimestamp = parseTimestamp(channel.last_pin_timestamp);
				state._guildId = action.guild.id;
				this.refreshMentionChannel(channel.id);
			}
		}
		this.notifyChange(undefined, {global: true});
	}
	handleLoadMessages(action: {channelId: string; isAfter?: boolean; messages: Array<WireMessage>}): void {
		const state = this.get(action.channelId);
		state.loadedMessages = true;
		const messages = Messages.getMessages(action.channelId);
		const newestMessage = messages.last();
		if (newestMessage != null && isNewerMessageId(newestMessage.id, state.lastMessageId)) {
			state.lastMessageId = newestMessage.id;
		}
		if (messages.hasPresent() || (messages.jumpTargetId != null && messages.jumpTargetId === state.ackMessageId)) {
			state.rebuild();
		} else if (action.isAfter && state.ackMessageId != null && messages.has(state.ackMessageId, true)) {
			state.unreadCount += action.messages.length;
		}
		this.notifyChange(action.channelId);
	}
	handleIncomingMessage(action: {channelId: string; message: WireMessage}): void {
		const state = this.get(action.channelId);
		const currentUser = Users.getCurrentUser();
		if (isNewerMessageId(action.message.id, state.lastMessageId)) {
			state.lastMessageId = action.message.id;
		}
		if (currentUser != null && action.message.author.id === currentUser.id) {
			this.cancelPendingAck(action.channelId);
			state.clearStickyUnread();
			this.applyAck(state, {messageId: action.message.id, local: true});
			this.notifyChange(action.channelId);
			return;
		}
		if (this.isAutomaticAckEnabled(action.channelId) && Dimension.isAtBottom(action.channelId)) {
			const result = this.applyAck(state, {messageId: action.message.id, preserveStickyUnread: true});
			if (result.acked && result.messageId != null) {
				this.queueAck(action.channelId, result.messageId, {
					immediate: false,
					hadMentions: result.hadMentions,
				});
			}
			this.notifyChange(action.channelId);
			return;
		}
		if (state.ackMessageId != null && compareMessageIds(action.message.id, state.ackMessageId) <= 0) {
			this.notifyChange(action.channelId);
			return;
		}
		if (state.oldestUnreadMessageId == null || state.oldestUnreadMessageIdStale) {
			state.oldestUnreadMessageId = action.message.id;
		}
		state.unreadCount++;
		if (currentUser != null && !Relationships.isBlocked(action.message.author.id)) {
			const shouldMention = state.shouldMentionFor(action.message, currentUser.id, state.isPrivate);
			if (shouldMention) {
				this.setMentionCount(state, state.mentionCount + 1);
			}
		}
		this.notifyChange(action.channelId);
	}
	handleMessageDelete(action: {channelId: string}): void {
		this.notifyChange(action.channelId);
	}
	handleChannelCreate(action: {channel: ChannelPayload}): void {
		if (
			action.channel.type !== ChannelTypes.DM &&
			action.channel.type !== ChannelTypes.GROUP_DM &&
			action.channel.type !== ChannelTypes.GUILD_TEXT &&
			action.channel.type !== ChannelTypes.DM_PERSONAL_NOTES
		) {
			return;
		}
		const state = this.get(action.channel.id);
		state.lastMessageId = action.channel.last_message_id ?? null;
		state.lastPinTimestamp = parseTimestamp(action.channel.last_pin_timestamp);
		state._guildId = action.channel.guild_id ?? null;
		const archivedState = this.archivedStates.get(action.channel.id as ChannelId);
		if (archivedState != null) {
			state.ackMessageId = archivedState.ackMessageId;
			state.ackPinTimestamp = archivedState.ackPinTimestamp;
			this.archivedStates.delete(action.channel.id as ChannelId);
		}
		if (
			(action.channel.type === ChannelTypes.DM ||
				action.channel.type === ChannelTypes.GROUP_DM ||
				action.channel.type === ChannelTypes.DM_PERSONAL_NOTES) &&
			action.channel.last_message_id != null
		) {
			state.ackMessageId = action.channel.last_message_id;
		} else if (action.channel.type === ChannelTypes.GUILD_TEXT && state.hasUnread() && state.unreadCount === 0) {
			state.estimated = true;
			state.unreadCount = Math.max(1, state.mentionCount);
		}
		this.notifyChange(action.channel.id);
	}
	handlePassiveLastMessageUpdates(channels: Record<string, string>): void {
		const changedChannels: Array<ChannelId> = [];
		for (const [channelId, lastMessageId] of Object.entries(channels)) {
			const state = this.states.get(channelId as ChannelId);
			if (state == null) continue;
			if (state.lastMessageId === lastMessageId) continue;
			state.lastMessageId = lastMessageId;
			if (state.hasUnread() && state.unreadCount === 0) {
				state.estimated = true;
				state.unreadCount = Math.max(1, state.mentionCount);
			}
			changedChannels.push(channelId as ChannelId);
		}
		if (changedChannels.length === 0) return;
		for (const channelId of changedChannels) {
			if (!this.pendingGlobalRecompute) {
				const entry = this.states.get(channelId);
				this.pendingChanges.set(channelId, (entry?.guildId ?? null) as GuildId | null);
			}
		}
		this.bumpVersion();
	}
	handleChannelDelete(action: {
		channel: {
			id: string;
			type?: number;
			guild_id?: string;
		};
	}): void {
		if (action.channel.guild_id != null && action.channel.type === ChannelTypes.GUILD_TEXT) {
			this.archiveState(action.channel.id);
		}
		this.clear(action.channel.id);
	}
	handleChannelAck(action: {channelId: string; messageId?: string; immediate?: boolean; force?: boolean}): void {
		const state = this.get(action.channelId);
		const result = this.applyAck(state, {
			messageId: action.messageId,
			immediate: action.immediate,
			force: action.force,
			isExplicitUserAction: true,
		});
		if (result.acked && result.messageId != null) {
			this.queueAck(action.channelId, result.messageId, {
				immediate: action.immediate || action.force,
				hadMentions: result.hadMentions,
			});
			this.notifyChange(action.channelId);
		}
	}
	handleBulkChannelAck(
		entries: Array<{
			channelId: string;
			messageId: string;
		}>,
	): void {
		if (entries.length === 0) return;
		const changedChannels: Array<string> = [];
		for (const entry of entries) {
			const state = this.get(entry.channelId);
			const result = this.applyAck(state, {
				messageId: entry.messageId,
				immediate: true,
				force: true,
				isExplicitUserAction: true,
			});
			if (!result.acked || result.messageId == null) continue;
			this.queueAck(entry.channelId, result.messageId, {immediate: true, hadMentions: result.hadMentions});
			changedChannels.push(entry.channelId);
		}
		if (changedChannels.length === 0) return;
		for (const channelId of changedChannels) {
			this.refreshMentionChannel(channelId);
			if (!this.pendingGlobalRecompute) {
				const entry = this.states.get(channelId as ChannelId);
				this.pendingChanges.set(channelId as ChannelId, (entry?.guildId ?? null) as GuildId | null);
			}
		}
		this.bumpVersion();
	}
	handleChannelAckWithStickyUnread(action: {channelId: string}): void {
		const state = this.get(action.channelId);
		const lastMessageId = state.lastMessageId;
		if (lastMessageId == null) {
			return;
		}
		const ackedMessageId = state.ackMessageId;
		const hasUnreads = state.unreadCount > 0;
		const ackBehind = ackedMessageId == null || compareMessageIds(ackedMessageId, lastMessageId) < 0;
		if (!hasUnreads && !ackBehind) {
			return;
		}
		const result = this.applyAck(state, {
			messageId: lastMessageId,
			preserveStickyUnread: true,
		});
		if (result.acked && result.messageId != null) {
			this.queueAck(action.channelId, result.messageId, {immediate: false, hadMentions: result.hadMentions});
			this.notifyChange(action.channelId);
		}
	}
	handleChannelPinsAck(action: {channelId: string; timestamp?: string}): void {
		const state = this.get(action.channelId);
		if (this.applyPinAck(state, action.timestamp)) {
			this.notifyChange(action.channelId);
		}
	}
	handleChannelPinsUpdate(action: {channelId: string; lastPinTimestamp: string}): void {
		const state = this.get(action.channelId);
		const newTimestamp = parseTimestamp(action.lastPinTimestamp);
		if (state.lastPinTimestamp !== newTimestamp) {
			state.lastPinTimestamp = newTimestamp;
			this.notifyChange(action.channelId);
		}
	}
	handleMessageAck(action: {channelId: string; messageId: string; mentionCount?: number; manual: boolean}): void {
		const state = this.get(action.channelId);
		const mentionCount = action.mentionCount;
		if (action.manual) {
			state.clearStickyUnread();
			state.isManualAck = true;
			state.rebuild(action.messageId, {recomputeMentions: true});
			this.cancelPendingAck(action.channelId);
			AutoAck.disableForChannel(action.channelId);
			if (mentionCount != null) {
				this.setMentionCount(state, mentionCount);
			}
			this.notifyChange(action.channelId);
			return;
		}
		if (state.ackMessageId != null && compareMessageIds(action.messageId, state.ackMessageId) < 0) {
			return;
		}
		if (action.messageId === state.ackMessageId) {
			if (mentionCount != null) {
				this.setMentionCount(state, mentionCount);
				this.notifyChange(action.channelId);
			}
			this.cancelPendingAckIfCovered(action.channelId, action.messageId);
			return;
		}
		const result = this.applyAck(state, {messageId: action.messageId, local: true});
		if (!result.acked) {
			return;
		}
		if (mentionCount != null) {
			this.setMentionCount(state, mentionCount);
		}
		this.cancelPendingAckIfCovered(action.channelId, action.messageId);
		this.notifyChange(action.channelId);
	}
	handleClearManualAck(action: {channelId: string}): void {
		const state = this.get(action.channelId);
		if (state.isManualAck) {
			state.isManualAck = false;
			this.notifyChange(action.channelId);
		}
	}
	subscribe(callback: () => void): () => void {
		this.listeners.add(callback);
		callback();
		return () => {
			this.listeners.delete(callback);
		};
	}
	async flushPendingAcks(): Promise<void> {
		await this.flushDueAcks(true);
	}
	private shouldAck(state: ReadStateEntry, force: boolean, local: boolean, isExplicitUserAction: boolean): boolean {
		if (force || local || isExplicitUserAction) {
			return true;
		}
		if (state.isManualAck) {
			return false;
		}
		if (!state.loadedMessages) {
			return false;
		}
		return true;
	}
	private applyAck(state: ReadStateEntry, options: AckOptions): AppliedAck {
		const {
			messageId,
			local = false,
			force = false,
			isExplicitUserAction = false,
			preserveStickyUnread = false,
		} = options;
		if (!this.shouldAck(state, force, local, isExplicitUserAction)) {
			return {acked: false, messageId: null, hadMentions: false};
		}
		if (!force && !local && !isExplicitUserAction && !state.canTrackUnreads()) {
			return {acked: false, messageId: null, hadMentions: false};
		}
		const finalMessageId = messageId ?? state.lastMessageId;
		if (finalMessageId == null) {
			return {acked: false, messageId: null, hadMentions: false};
		}
		if (!force && state.ackMessageId != null && compareMessageIds(finalMessageId, state.ackMessageId) < 0) {
			return {acked: false, messageId: null, hadMentions: false};
		}
		const hadMentions = state.hasMentions();
		if (preserveStickyUnread && state.oldestUnreadMessageId != null && state.stickyUnreadMessageId == null) {
			state.stickyUnreadMessageId = state.oldestUnreadMessageId;
		}
		state.estimated = false;
		state.unreadCount = 0;
		this.setMentionCount(state, 0);
		state.ackMessageId = finalMessageId;
		state.oldestUnreadMessageId = null;
		if (local || force || isExplicitUserAction) {
			state.isManualAck = false;
			state.clearStickyUnread();
		}
		this.cancelPendingAckIfCovered(state.channelId, finalMessageId);
		return {acked: true, messageId: finalMessageId, hadMentions};
	}
	private applyPinAck(state: ReadStateEntry, timestamp?: string | null): boolean {
		const newTimestamp = timestamp == null ? state.lastPinTimestamp : parseTimestamp(timestamp);
		const ackTimestamp = newTimestamp !== 0 ? newTimestamp : state.lastPinTimestamp;
		if (state.ackPinTimestamp === ackTimestamp) {
			return false;
		}
		state.ackPinTimestamp = ackTimestamp;
		return true;
	}
	private queueAck(
		channelId: string,
		messageId: string,
		options: {
			immediate?: boolean;
			hadMentions?: boolean;
		},
	): void {
		const now = Date.now();
		const delay = options.immediate || options.hadMentions ? 0 : ACK_BATCH_DELAY_MS;
		const deadline = now + delay;
		const existing = this.pendingAcks.get(channelId as ChannelId);
		const existingIsNewer = existing != null && compareMessageIds(existing.messageId, messageId) > 0;
		const pending: PendingAck = {
			channelId,
			messageId: existingIsNewer ? existing.messageId : messageId,
			deadline: existing == null ? deadline : Math.min(existing.deadline, deadline),
			attempt: existing?.attempt ?? 0,
		};
		this.pendingAcks.set(channelId as ChannelId, pending);
		const state = this.getIfExists(channelId);
		if (state != null) {
			state.outgoingAck = pending.messageId;
		}
		this.scheduleAckFlush();
	}
	private clearAckFlushTimer(): void {
		if (this.ackFlushTimer != null) {
			clearTimeout(this.ackFlushTimer);
			this.ackFlushTimer = null;
		}
	}
	private scheduleAckFlush(): void {
		this.clearAckFlushTimer();
		if (this.pendingAcks.size === 0 || this.isFlushingAcks) {
			return;
		}
		let nextDeadline = Number.POSITIVE_INFINITY;
		for (const pending of this.pendingAcks.values()) {
			nextDeadline = Math.min(nextDeadline, pending.deadline);
		}
		const delay = Math.max(0, nextDeadline - Date.now());
		this.ackFlushTimer = setTimeout(() => {
			this.ackFlushTimer = null;
			void this.flushDueAcks(false);
		}, delay);
	}
	private cancelPendingAck(channelId: string): void {
		const deleted = this.pendingAcks.delete(channelId as ChannelId);
		const state = this.getIfExists(channelId);
		if (state != null) {
			state.outgoingAck = null;
		}
		if (deleted) {
			this.scheduleAckFlush();
		}
	}
	private cancelPendingAckIfCovered(channelId: string, ackedMessageId: string): void {
		const pending = this.pendingAcks.get(channelId as ChannelId);
		if (pending == null) return;
		if (compareMessageIds(ackedMessageId, pending.messageId) < 0) return;
		this.cancelPendingAck(channelId);
	}
	private async flushDueAcks(force: boolean): Promise<void> {
		if (this.isFlushingAcks) {
			return;
		}
		this.clearAckFlushTimer();
		const now = Date.now();
		const due = Array.from(this.pendingAcks.values()).filter((pending) => force || pending.deadline <= now);
		if (due.length === 0) {
			this.scheduleAckFlush();
			return;
		}
		this.isFlushingAcks = true;
		try {
			for (const chunk of chunkEntries(due, ACK_BATCH_SIZE)) {
				await this.sendAckChunk(chunk);
			}
		} finally {
			this.isFlushingAcks = false;
			this.scheduleAckFlush();
		}
	}
	private async sendAckChunk(entries: Array<PendingAck>): Promise<void> {
		try {
			await conduit.post(Endpoints.READ_STATES_ACK_BULK, {
				body: {
					read_states: entries.map((entry) => ({
						channel_id: entry.channelId,
						message_id: entry.messageId,
					})),
				},
			});
			for (const entry of entries) {
				const current = this.pendingAcks.get(entry.channelId as ChannelId);
				if (current?.messageId === entry.messageId) {
					this.pendingAcks.delete(entry.channelId as ChannelId);
					const state = this.getIfExists(entry.channelId);
					if (state?.outgoingAck === entry.messageId) {
						state.outgoingAck = null;
					}
				}
			}
		} catch (error) {
			logger.error('Failed to flush read-state acknowledgements:', error);
			const now = Date.now();
			for (const entry of entries) {
				const current = this.pendingAcks.get(entry.channelId as ChannelId);
				if (current == null || current.messageId !== entry.messageId) continue;
				const attempt = current.attempt + 1;
				const retryDelay = Math.min(ACK_RETRY_MAX_DELAY_MS, ACK_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1));
				current.attempt = attempt;
				current.deadline = now + retryDelay;
			}
		}
	}
}
export default new ReadStates();
